import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import KeniOrb from '@/components/wrapped/KeniOrb';
import ChatBubble from '@/north-star/ChatBubble';
import { speak } from '@/north-star/speak';
import {
  CLAIM,
  CLAIM_DONE,
  CLAIM_HANDOFF,
  CLAIM_HANDOFF_ITEMS,
  CLAIM_INTRO,
  CLAIM_QUESTIONS,
} from '@/north-star/ClaimChat.content';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The Attendance Allowance claim, done as a Keni conversation. She asks the
// questions she can; the bits she can't do over chat (a photo, a signature) are
// handed off to tasks. The claim then sits "Ready to submit" under Claims.
export type ClaimChatProps = {
  /** Leave the claim (the top-left close). */
  onClose: () => void;
  /** Finished asking — Keni's added the rest to tasks; go and see them. */
  onDone: () => void;
};

type Msg = { from: 'keni' | 'user'; text: string };

const ClaimChat = ({ onClose, onDone }: ClaimChatProps) => {
  const [messages, setMessages] = useState<Msg[]>(() => [
    { from: 'keni', text: CLAIM_INTRO },
    { from: 'keni', text: CLAIM_QUESTIONS[0].keni },
  ]);
  const [index, setIndex] = useState(0);
  const [text, setText] = useState('');
  const [done, setDone] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  const current = CLAIM_QUESTIONS[index];

  const answer = (value: string) => {
    if (done) return;
    const next = index + 1;
    const additions: Msg[] = [{ from: 'user', text: value }];
    if (next < CLAIM_QUESTIONS.length) {
      additions.push({ from: 'keni', text: CLAIM_QUESTIONS[next].keni });
      setIndex(next);
    } else {
      // Out of askable questions — hand the rest off to tasks.
      additions.push({ from: 'keni', text: CLAIM_HANDOFF });
      CLAIM_HANDOFF_ITEMS.forEach((item) =>
        additions.push({ from: 'keni', text: `• ${item}` }),
      );
      additions.push({ from: 'keni', text: CLAIM_DONE });
      setDone(true);
    }
    setMessages((prev) => [...prev, ...additions]);
  };

  const submitText = () => {
    const value = text.trim();
    if (!value) return;
    setText('');
    answer(value);
  };

  return (
    <ScreenRoot>
      <StatusBar />

      {/* header: title + leave */}
      <Stack
        sx={{ alignItems: 'center', px: 2, py: 1.5 }}
        direction='row'
        spacing={1}
      >
        <IconButton aria-label='Leave claim' edge='start' onClick={onClose}>
          <Icon icon={'xmark' as never} fontSize={16} />
        </IconButton>
        <Typography variant='subtitle2'>{CLAIM.title}</Typography>
      </Stack>
      <Divider />

      {/* transcript */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: '72px' }}>
        <Stack spacing={1.5}>
          {messages.map((m, i) => (
            <ChatBubble
              key={i}
              from={m.from}
              text={m.text}
              onReplay={m.from === 'keni' ? () => speak(m.text) : undefined}
            />
          ))}
          <Box ref={bottomRef} />
        </Stack>
      </Box>

      {/* bottom bar — orb straddles the top edge */}
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pb: 2,
          borderTop: 1,
          borderColor: 'custom.neutral.200',
          bgcolor: 'background.paper',
          pt: '52px',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <KeniOrb size={80} state={done ? 'speaking' : 'listening'} />
        </Box>

        {done ? (
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={CLAIM.doneCta}
            onPress={onDone}
          >
            {CLAIM.doneCta}
          </Button>
        ) : (
          <Stack spacing={1.5}>
            {/* quick replies for short-answer questions */}
            {current?.replies && (
              <Stack
                sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
                direction='row'
                spacing={1}
                useFlexGap
              >
                {current.replies.map((r) => (
                  <Chip key={r} label={r} clickable onClick={() => answer(r)} />
                ))}
              </Stack>
            )}

            <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
              <TextField
                fullWidth
                size='small'
                variant='outlined'
                placeholder={current?.placeholder ?? CLAIM.placeholder}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitText();
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: `${radius.xxl}px`,
                  },
                  '& .MuiOutlinedInput-input': { pl: 2 },
                }}
              />
              <IconButton
                aria-label='Send'
                onClick={submitText}
                sx={{
                  flexShrink: 0,
                  bgcolor: 'custom.coral.600',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'custom.coral.700' },
                }}
              >
                <Icon icon={'arrow-up' as never} fontSize={16} />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </ScreenRoot>
  );
};

ClaimChat.displayName = 'ClaimChat';
export default ClaimChat;
